/**
 * Public Float Plan Share Page
 * 
 * Displays a shared float plan (read-only, no auth required)
 * Shows destination, waypoints, expected return, and vessel info
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import { Icon, divIcon } from 'leaflet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Navigation, Clock, Anchor, Ship, AlertTriangle, 
  MapPin, FileText, User, Phone 
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

// Boat icon for current position
const boatIcon = new Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="#0ea5e9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1 .6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/>
      <path d="M19.38 20A11.6 11.6 0 0 0 21 14l-9-4-9 4c0 2.9.94 5.34 2.81 7.76"/>
      <path d="M12 2v8"/>
      <path d="M12 6l4 2"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

// Waypoint marker
const createWaypointIcon = (number, isDestination = false) => {
  const color = isDestination ? '#ef4444' : '#0ea5e9';
  return divIcon({
    className: 'custom-waypoint-icon',
    html: `<div style="background:${color};color:white;width:24px;height:24px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:bold;border:2px solid white;box-shadow:0 2px 4px rgba(0,0,0,0.3)">${number}</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
};

const FloatPlanSharePage = () => {
  const { token } = useParams();
  const [shareData, setShareData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  
  const fetchShareData = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/share/plan/${token}`);
      
      if (response.status === 404) {
        setError('This float plan share link has expired or been revoked.');
        setLoading(false);
        return;
      }
      
      if (!response.ok) {
        setError('Failed to load shared float plan.');
        setLoading(false);
        return;
      }
      
      const data = await response.json();
      setShareData(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching share data:', err);
      setError('Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [API_BASE, token]);
  
  // Initial fetch
  useEffect(() => {
    fetchShareData();
  }, [fetchShareData]);
  
  // Auto-refresh every 30 seconds if on trip
  useEffect(() => {
    if (!shareData || !shareData.is_on_trip) return;
    
    const interval = setInterval(fetchShareData, 30000);
    return () => clearInterval(interval);
  }, [shareData, fetchShareData]);
  
  // Calculate map center and bounds
  const getMapCenter = () => {
    if (shareData?.current_lat && shareData?.current_lon) {
      return [shareData.current_lat, shareData.current_lon];
    }
    if (shareData?.destination_lat && shareData?.destination_lon) {
      return [shareData.destination_lat, shareData.destination_lon];
    }
    // Default to first waypoint if available
    if (shareData?.waypoints?.length > 0) {
      const wp = shareData.waypoints[0];
      if (wp.lat && wp.lon) {
        return [wp.lat, wp.lon];
      }
    }
    return [25.7617, -80.1918]; // Default to Miami
  };
  
  // Build route line from waypoints
  const getRouteLine = () => {
    if (!shareData?.waypoints?.length) return [];
    
    const points = [];
    
    // Add current position if on trip
    if (shareData.is_on_trip && shareData.current_lat && shareData.current_lon) {
      points.push([shareData.current_lat, shareData.current_lon]);
    }
    
    // Add waypoints
    shareData.waypoints.forEach(wp => {
      if (wp.lat && wp.lon) {
        points.push([wp.lat, wp.lon]);
      }
    });
    
    // Add destination
    if (shareData.destination_lat && shareData.destination_lon) {
      points.push([shareData.destination_lat, shareData.destination_lon]);
    }
    
    return points;
  };
  
  // Format expected return
  const formatExpectedReturn = () => {
    if (!shareData?.expected_return_at) return 'Not specified';
    try {
      const date = new Date(shareData.expected_return_at);
      return date.toLocaleString();
    } catch {
      return 'Unknown';
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <FileText className="w-12 h-12 text-cyan-400 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-400">Loading float plan...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-gray-800 border-gray-700">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-orange-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Float Plan Not Available</h2>
            <p className="text-gray-400">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  const routeLine = getRouteLine();
  
  return (
    <div className="min-h-screen bg-gray-900 flex flex-col" data-testid="float-plan-share-page">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 py-3">
        <div className="flex items-center justify-between max-w-screen-xl mx-auto">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-cyan-400" />
            <div>
              <h1 className="text-lg font-bold text-white">
                {shareData?.display_name || 'Boater'}&apos;s Float Plan
              </h1>
              <p className="text-xs text-gray-400">
                {shareData?.destination}
              </p>
            </div>
          </div>
          
          {shareData?.is_on_trip ? (
            <Badge className="bg-green-500">On Trip</Badge>
          ) : (
            <Badge className="bg-gray-500">Planned</Badge>
          )}
        </div>
      </div>
      
      {/* Map */}
      <div className="h-64 md:h-80 relative">
        <MapContainer
          center={getMapCenter()}
          zoom={10}
          style={{ height: '100%', width: '100%' }}
          zoomControl={false}
        >
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            attribution='&copy; OpenStreetMap'
          />
          
          {/* Route line */}
          {routeLine.length >= 2 && (
            <Polyline
              positions={routeLine}
              color="#0ea5e9"
              weight={3}
              opacity={0.7}
              dashArray="10, 10"
            />
          )}
          
          {/* Current position marker */}
          {shareData?.is_on_trip && shareData?.current_lat && shareData?.current_lon && (
            <Marker 
              position={[shareData.current_lat, shareData.current_lon]} 
              icon={boatIcon}
            >
              <Popup>
                <div className="text-center">
                  <strong>{shareData.display_name}</strong>
                  <p className="text-sm">Current Position</p>
                </div>
              </Popup>
            </Marker>
          )}
          
          {/* Waypoint markers */}
          {shareData?.waypoints?.map((wp, index) => (
            wp.lat && wp.lon && (
              <Marker
                key={index}
                position={[wp.lat, wp.lon]}
                icon={createWaypointIcon(index + 1)}
              >
                <Popup>
                  <strong>{wp.name || `Waypoint ${index + 1}`}</strong>
                </Popup>
              </Marker>
            )
          ))}
          
          {/* Destination marker */}
          {shareData?.destination_lat && shareData?.destination_lon && (
            <Marker
              position={[shareData.destination_lat, shareData.destination_lon]}
              icon={createWaypointIcon('D', true)}
            >
              <Popup>
                <strong>{shareData.destination}</strong>
              </Popup>
            </Marker>
          )}
        </MapContainer>
      </div>
      
      {/* Info Panel */}
      <div className="flex-1 bg-gray-800 p-4 overflow-auto">
        <div className="max-w-screen-xl mx-auto space-y-4">
          {/* Trip Status */}
          {shareData?.is_on_trip && shareData?.trip_started_at && (
            <Card className="bg-gray-700 border-gray-600">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Ship className="w-5 h-5 text-green-400" />
                  <div>
                    <p className="text-sm text-gray-400">Trip Started</p>
                    <p className="text-white font-medium">
                      {formatDistanceToNow(new Date(shareData.trip_started_at), { addSuffix: true })}
                    </p>
                  </div>
                  {shareData?.last_updated && (
                    <div className="ml-auto text-right">
                      <p className="text-sm text-gray-400">Last Update</p>
                      <p className="text-white font-medium">
                        {formatDistanceToNow(new Date(shareData.last_updated), { addSuffix: true })}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Destination & Expected Return */}
          <Card className="bg-gray-700 border-gray-600">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-white flex items-center gap-2">
                <MapPin className="w-4 h-4 text-cyan-400" />
                Destination
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-white text-lg font-medium">{shareData?.destination}</p>
              
              <div className="flex items-center gap-2 text-gray-400">
                <Clock className="w-4 h-4" />
                <span>Expected Return: {formatExpectedReturn()}</span>
              </div>
            </CardContent>
          </Card>
          
          {/* Waypoints */}
          {shareData?.waypoints?.length > 0 && (
            <Card className="bg-gray-700 border-gray-600">
              <CardHeader className="pb-2">
                <CardTitle className="text-base text-white flex items-center gap-2">
                  <Navigation className="w-4 h-4 text-cyan-400" />
                  Route ({shareData.waypoints.length} waypoints)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {shareData.waypoints.map((wp, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-2 text-gray-300 text-sm"
                  >
                    <span className="w-5 h-5 rounded-full bg-cyan-500 text-white text-xs flex items-center justify-center">
                      {index + 1}
                    </span>
                    <span>{wp.name || `Waypoint ${index + 1}`}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          
          {/* Vessel Info */}
          {shareData?.vessel_info && (
            <Card className="bg-gray-700 border-gray-600">
              <CardHeader className="pb-2">
                <CardTitle className="text-base text-white flex items-center gap-2">
                  <Anchor className="w-4 h-4 text-cyan-400" />
                  Vessel Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-300">{shareData.vessel_info}</p>
              </CardContent>
            </Card>
          )}
          
          {/* Privacy Notice */}
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-3">
              <p className="text-xs text-gray-500 text-center">
                This float plan was shared by the boater. Link expires{' '}
                {shareData?.share_expires_at && (
                  formatDistanceToNow(new Date(shareData.share_expires_at), { addSuffix: true })
                )}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FloatPlanSharePage;
